import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { createPortal } from 'react-dom';
import { WebhookConfiguration } from 'shared/types/ObjectTypes';
import { useTranslation, Trans } from 'react-i18next';
import { inputClasses, buttonClasses } from 'styles/tailwindClassnames';
import ModalShell from 'shared/components/ui/ModalShell';
import { HoverTooltip } from 'shared/components/ui/HoverTooltip';
import { Icon } from 'react-icons-kit';
import { CodeBuilderToggle } from 'shared/components/inputs/CodeBuilderToggle';
import { ic_fullscreen } from 'react-icons-kit/md/ic_fullscreen';
import { toast } from 'react-toastify';
import JqFilterBuilder from './JqFilterBuilder';
import JsonSchemaBuilder from './JsonSchemaBuilder';
import {
  parseJsonSchemaToFields,
  generateSampleFromSchema,
  mergeSampleWithSchema,
  inferSchemaFromSample,
} from './schema-builder/helpers';
import { MONACO_OPTIONS } from './schema-builder/editorConfig';
import { SelectMenu } from './schema-builder/SelectMenu';
import { StepHeader } from './webhook-common/StepHeader';
import { CollapsibleSection } from './webhook-common/CollapsibleSection';
import { EditorExpandModal } from './webhook-common/EditorExpandModal';
import { SampleToSchemaPanel } from './webhook-common/SampleToSchemaPanel';

type ValidationErrors = {
  json_schema?: string;
  diagnostic_destination_url?: string;
};

// ── Main component ───────────────────────────────────────────────────────────

type RouteDetailsWebhookProps = {
  webhookConfiguration: WebhookConfiguration;
  currentMemberIsAdmin: boolean;
  onSave: (data: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  changesMade: boolean;
  onChangesMade: () => void;
};

// This editor used to be its own full-page screen with a Save bar fixed to the
// viewport. It now renders inside a SelectionPanel, which draws the Save/Cancel
// footer for every other section — so the panel drives this one too, and reaches
// the draft (which lives in this component's state) through here.
export type RouteDetailsWebhookHandle = { save: () => Promise<void> };

const RouteDetailsWebhook = forwardRef<
  RouteDetailsWebhookHandle,
  RouteDetailsWebhookProps
>(
  (
    {
      webhookConfiguration,
      currentMemberIsAdmin,
      onSave,
      onCancel,
      changesMade,
      onChangesMade,
    }: RouteDetailsWebhookProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const { data } = webhookConfiguration;

    // ── State ──
    const [jqFilter, setJqFilter] = useState<string>(data?.jq_filter ?? '');
    const [jsonSchemaText, setJsonSchemaText] = useState<string>(
      data?.json_schema != null
        ? JSON.stringify(data.json_schema, null, 2)
        : '',
    );
    const SUPPORTED_OUTPUT_TYPES = ['obv', 'ev'];
    const [outputType, setOutputType] = useState<string>(() => {
      return data?.output_type || 'obv';
    });
    const [diagnosticUrl, setDiagnosticUrl] = useState<string>(
      data?.diagnostic_destination_url ?? '',
    );
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
      {},
    );
    const [expandedField, setExpandedField] = useState<'json_schema' | null>(
      null,
    );
    const [schemaMode, setSchemaMode] = useState<'code' | 'builder'>('builder');
    const [showSamplePanel, setShowSamplePanel] = useState(false);
    const [samplePayload, setSamplePayload] = useState<object | undefined>(
      () => {
        if (data?.json_schema) {
          const fields = parseJsonSchemaToFields(data.json_schema);
          if (fields) return generateSampleFromSchema(fields);
        }
        return undefined;
      },
    );
    const [parsedExternalSchema, setParsedExternalSchema] = useState<
      object | null
    >(null);
    const [schemaEditorKey, setSchemaEditorKey] = useState(0);

    const jsonSchemaEditorRef = useRef<editor.IStandaloneCodeEditor | null>(
      null,
    );

    // Resync local state only when navigating to a different webhook config, not on
    // parent re-renders that pass the same config with a new object reference (e.g. after
    // onChangesMade). Safe because the parent treats this config as immutable during the
    // editing session — server refetches create a new id/component mount.
    useEffect(() => {
      setJqFilter(data?.jq_filter ?? '');
      setJsonSchemaText(
        data?.json_schema != null
          ? JSON.stringify(data.json_schema, null, 2)
          : '',
      );
      setOutputType(data?.output_type || 'obv');
      setDiagnosticUrl(data?.diagnostic_destination_url ?? '');
      setValidationErrors({});
      setShowSamplePanel(false);
    }, [webhookConfiguration.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers ──
    function handleSchemaEditorChange(v: string) {
      setJsonSchemaText(v);
      onChangesMade();
      try {
        const parsed = JSON.parse(v);
        if (
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed) &&
          parsed.type === 'object'
        ) {
          setParsedExternalSchema(parsed);
          const fields = parseJsonSchemaToFields(parsed);
          if (fields) {
            setSamplePayload(mergeSampleWithSchema(fields, samplePayload));
          }
        }
      } catch {
        /* not valid JSON yet */
      }
    }

    function handleBuilderGenerate(schema: object) {
      const schemaText = JSON.stringify(schema, null, 2);
      setJsonSchemaText(schemaText);
      setSchemaEditorKey((key) => key + 1);
      const fields = parseJsonSchemaToFields(schema);
      if (fields) {
        setSamplePayload(mergeSampleWithSchema(fields, samplePayload));
      }
      onChangesMade();
    }

    function handleGenerateSchemaFromSample(sampleJson: string) {
      try {
        const parsed = JSON.parse(sampleJson);
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          Array.isArray(parsed)
        )
          return;
        const schema = inferSchemaFromSample(parsed);
        const schemaText = JSON.stringify(schema, null, 2);
        // Force a new state reference so React always re-renders and Monaco receives the updated value
        setJsonSchemaText(schemaText);
        setParsedExternalSchema(schema);
        setSamplePayload(parsed);
        setSchemaEditorKey((k) => k + 1);
        onChangesMade();
      } catch {
        /* invalid JSON */
      }
    }

    function validate(): {
      valid: boolean;
      errors: ValidationErrors;
      parsedJsonSchema: any;
    } {
      const errors: ValidationErrors = {};
      let parsedJsonSchema: any = undefined;
      if (jsonSchemaText.trim()) {
        try {
          parsedJsonSchema = JSON.parse(jsonSchemaText);
          if (
            typeof parsedJsonSchema !== 'object' ||
            parsedJsonSchema === null ||
            Array.isArray(parsedJsonSchema)
          ) {
            errors.json_schema = t('Must be a valid JSON schema object.');
          }
        } catch {
          errors.json_schema = t('Must be valid JSON.');
        }
      }
      if (diagnosticUrl.trim()) {
        try {
          new URL(diagnosticUrl);
        } catch {
          errors.diagnostic_destination_url = t('Must be a valid URL.');
        }
      }
      return {
        valid: Object.keys(errors).length === 0,
        errors,
        parsedJsonSchema,
      };
    }

    useImperativeHandle(ref, () => ({ save: handleSave }));

    async function handleSave() {
      const { valid, errors, parsedJsonSchema } = validate();
      setValidationErrors(errors);
      if (!valid) return;
      // The in-flight flag lives with the footer that owns the button now.
      try {
        await onSave({
          jq_filter: jqFilter,
          json_schema: parsedJsonSchema,
          output_type: outputType,
          diagnostic_destination_url: diagnosticUrl,
        });
      } catch {
        toast.error(t('Failed to save webhook configuration'), {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    }

    // ── Style helpers ──
    const fieldLabel = 'text-body-sm text-ui-text-primary-text';
    const fieldError = 'mt-1 text-body-sm text-ui-status-error-destructive';
    const expandBtn =
      'text-ui-text-primary-text hover:text-ui-cta-primary transition-colors px-1 flex items-center border-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-t100 rounded';

    return (
      <div className="mt-3 flex h-full flex-col">
        <div className="flex w-full flex-col">
          <div className="flex w-full flex-col gap-8 p-[16px]">
            {/* ── Step 1: Webhook Schema ── */}
            <section>
              <StepHeader step={1} title={t('Webhook Schema')} />
              <p className="mb-4 text-body-sm text-ui-text-primary-text">
                <Trans
                  i18nKey="webhookSchemaDescription"
                  components={{ bold: <strong /> }}
                />
              </p>

              {/* The fields are long, so they stay folded until wanted. An empty
                  schema starts open, so Generate from Sample and the first
                  field are in view. Keyed on the config so another one picks
                  its own starting state. */}
              <CollapsibleSection
                key={webhookConfiguration.id}
                title={t('Schema')}
                defaultOpen={
                  Object.keys(data?.json_schema?.properties ?? {}).length === 0
                }
                keepMounted
                testId="RouteDetailsWebhook-schema-toggle"
              >
                <div className="p-4">
                  {/* Controls toolbar — borderless, sits above the content box */}
                  <div className="mb-2 flex items-center justify-end gap-2">
                    {currentMemberIsAdmin && (
                      <button
                        type="button"
                        onClick={() => setShowSamplePanel(true)}
                        className={buttonClasses.outlineXs}
                      >
                        {t('Generate from sample')}
                      </button>
                    )}
                    {schemaMode === 'code' && (
                      <HoverTooltip
                        content={t('Expand editor')}
                        focusable={false}
                        portal
                      >
                        <button
                          className={expandBtn}
                          onClick={() => setExpandedField('json_schema')}
                          aria-label={t('Expand editor')}
                          type="button"
                        >
                          <Icon icon={ic_fullscreen} size={22} />
                        </button>
                      </HoverTooltip>
                    )}
                    <CodeBuilderToggle
                      isCodeMode={schemaMode === 'code'}
                      onToggle={() => {
                        if (schemaMode === 'code') {
                          setSchemaMode('builder');
                        } else {
                          setSchemaMode('code');
                          setSchemaEditorKey((k) => k + 1);
                        }
                      }}
                      dataTestId="RouteDetailsWebhook-schema-mode-toggle"
                    />
                  </div>

                  <div className="flex flex-col overflow-hidden rounded-lg border border-ui-divider-lines">
                    {/* Content */}
                    <div className="bg-white">
                      {schemaMode === 'code' ? (
                        <div
                          className={`${validationErrors.json_schema ? 'border border-ui-status-error-destructive' : ''}`}
                        >
                          <div
                            style={{
                              height: 360,
                              resize: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            <Editor
                              key={schemaEditorKey}
                              height="100%"
                              language="json"
                              value={jsonSchemaText}
                              theme="vs"
                              options={MONACO_OPTIONS(!currentMemberIsAdmin)}
                              onMount={(e) => {
                                jsonSchemaEditorRef.current = e;
                              }}
                              onChange={(v) =>
                                handleSchemaEditorChange(v ?? '')
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="p-4">
                          <JsonSchemaBuilder
                            onGenerate={handleBuilderGenerate}
                            readOnly={!currentMemberIsAdmin}
                            initialSchema={data?.json_schema}
                            externalSchema={parsedExternalSchema}
                            hideHeader
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
              {/* Outside the section and the mode switch: a schema error has
                  to show whether the section is open and whichever tab is. */}
              {validationErrors.json_schema && (
                <p className={fieldError + ' mt-2'}>
                  {validationErrors.json_schema}
                </p>
              )}
            </section>

            {/* ── Step 2: Transformation ── */}
            <section>
              <StepHeader step={2} title={t('Transformation')} />
              <p className="mb-4 text-body-sm text-ui-text-primary-text">
                {t(
                  'Map incoming data fields to Gundi objects. Use the visual builder or edit the transformation code directly.',
                )}
              </p>

              <div className="mb-5 flex items-center gap-3">
                <label className="text-body-sm font-normal text-ui-text-primary-text">
                  {t('Creating')}:
                </label>
                {SUPPORTED_OUTPUT_TYPES.includes(outputType) ? (
                  <SelectMenu
                    value={outputType}
                    options={[
                      { value: 'obv', label: t('Observation') },
                      { value: 'ev', label: t('Event') },
                    ]}
                    onChange={(v) => {
                      setOutputType(v);
                      onChangesMade();
                    }}
                    disabled={!currentMemberIsAdmin}
                    className="w-44"
                    dataTestId="output-type-select"
                    ariaLabel={t('Creating')}
                  />
                ) : (
                  <span className="rounded border border-ui-field-outline bg-greyscale-11 px-2 py-1 font-mono text-body-sm text-ui-text-primary-text">
                    {outputType}
                  </span>
                )}
              </div>

              <JqFilterBuilder
                key={outputType}
                onGenerate={(jq) => {
                  setJqFilter(jq);
                  onChangesMade();
                }}
                onCodeChange={(jq) => {
                  setJqFilter(jq);
                  onChangesMade();
                }}
                onSampleInputChange={setSamplePayload}
                readOnly={!currentMemberIsAdmin}
                initialJq={jqFilter}
                initialSampleInput={samplePayload}
                objectType={outputType}
              />
            </section>

            {/* ── Advanced Settings ── */}
            <CollapsibleSection
              title={t('Advanced Settings')}
              defaultOpen={false}
              testId="RouteDetailsWebhook-advanced-settings-toggle"
            >
              <div className="flex flex-col gap-4 p-4">
                <div className="flex flex-col">
                  <label className={`${fieldLabel} mb-1`}>
                    {t('Diagnostic Tool URL')}
                  </label>
                  <input
                    type="text"
                    value={diagnosticUrl}
                    onChange={(e) => {
                      setDiagnosticUrl(e.target.value);
                      onChangesMade();
                    }}
                    className={`${validationErrors.diagnostic_destination_url ? inputClasses.error : inputClasses.standard} w-full`}
                    placeholder="https://..."
                    disabled={!currentMemberIsAdmin}
                    data-testid="RouteDetailsWebhook-diagnostic-url-input"
                  />
                  {validationErrors.diagnostic_destination_url && (
                    <p className={fieldError}>
                      {validationErrors.diagnostic_destination_url}
                    </p>
                  )}
                </div>
              </div>
            </CollapsibleSection>
          </div>
        </div>

        {/* Expand modal */}
        <EditorExpandModal
          isOpen={expandedField === 'json_schema'}
          title={t('Webhook Schema')}
          language="json"
          value={jsonSchemaText}
          onChange={(v) => handleSchemaEditorChange(v ?? '')}
          readOnly={!currentMemberIsAdmin}
          onClose={() => setExpandedField(null)}
        />

        {/* Generate from sample modal — portaled to <body> so its backdrop covers the nav bar */}
        {showSamplePanel &&
          currentMemberIsAdmin &&
          createPortal(
            <ModalShell
              title={t('Generate from sample')}
              onClose={() => setShowSamplePanel(false)}
            >
              <SampleToSchemaPanel
                onGenerate={(s) => {
                  handleGenerateSchemaFromSample(s);
                  setShowSamplePanel(false);
                }}
              />
            </ModalShell>,
            document.body,
          )}
      </div>
    );
  },
);

RouteDetailsWebhook.displayName = 'RouteDetailsWebhook';

export default RouteDetailsWebhook;
